import React, { useMemo, useState } from 'react';

export type SCIPhysicalOwner = {
  lastname: string;
  // A list from the batch query, but the per-SIREN API passes the registry value through: it can be a plain string
  firstnames: string[] | string | null;
  birthdayDate?: string;
  role?: string;
};

export const ownerFirstnames = (owner: SCIPhysicalOwner): string[] => {
  if (Array.isArray(owner.firstnames)) {
    return owner.firstnames.map((name) => String(name).trim()).filter(Boolean);
  }
  if (typeof owner.firstnames === 'string') {
    return owner.firstnames.split(',').map((name) => name.trim()).filter(Boolean);
  }

  return [];
};

interface PhysicalOwnersTableProps {
  owners: SCIPhysicalOwner[];
  // When set, each row gets a "Choisir" button (contact qualification: pre-fill the contact from an officer)
  onSelect?: (owner: SCIPhysicalOwner) => void;
  selectedOwner?: SCIPhysicalOwner | null;
}

const calculateAge = (birthdayDate: string): number | null => {
  if (!birthdayDate) {return null;}
  const today = new Date();
  const birthDate = new Date(birthdayDate);
  if (isNaN(birthDate.getTime())) {return null;}
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
};

const PhysicalOwnersTable: React.FC<PhysicalOwnersTableProps> = ({ owners, onSelect, selectedOwner }) => {
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOwners = useMemo(() => {
    if (!normalizedQuery) {return owners;}
    return owners.filter((o) => {
      const lastname = (o.lastname || '').toLowerCase();
      const firstnames = ownerFirstnames(o).join(' ').toLowerCase();
      const role = (o.role || '').toLowerCase();
      const birthday = (o.birthdayDate || '').toLowerCase();
      return (
        lastname.includes(normalizedQuery)
        || firstnames.includes(normalizedQuery)
        || role.includes(normalizedQuery)
        || birthday.includes(normalizedQuery)
      );
    });
  }, [owners, normalizedQuery]);

  return (
    <div className="overflow-x-scroll">
      <div className="flex items-center p-2">
        <input
          type="search"
          placeholder="Rechercher (nom, prénom, rôle, date de naissance)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded border px-2 py-1 text-sm"
        />
      </div>
      <table className="min-w-full divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
        <thead className="bg-gray-50">
          <tr>
            {onSelect && <th className="px-3 py-3"><span className="sr-only">Choisir</span></th>}
            <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Nom</th>
            <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Prénom(s)</th>
            <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Date de naissance</th>
            <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Rôle</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {filteredOwners.map((owner, index) => (
            <tr key={index} className={selectedOwner === owner ? 'bg-blue-50' : undefined}>
              {onSelect && (
                <td className="px-3 py-4">
                  <button
                    type="button"
                    onClick={() => onSelect(owner)}
                    aria-pressed={selectedOwner === owner}
                    className={`rounded border px-2 py-1 text-xs whitespace-nowrap ${selectedOwner === owner ? 'border-rgpd-blue bg-rgpd-blue text-white' : 'border-rgpd-blue text-rgpd-blue hover:bg-blue-50'}`}
                  >
                    {selectedOwner === owner ? 'Choisi' : 'Choisir'}
                  </button>
                </td>
              )}
              <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                {owner.lastname || '-'}
              </td>
              <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                {ownerFirstnames(owner).length > 0 ? ownerFirstnames(owner).join(', ') : '-'}
              </td>
              <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                {owner.birthdayDate ? (
                  <>
                    {owner.birthdayDate}
                    {calculateAge(owner.birthdayDate) !== null && (
                      <span className="ml-2 text-gray-500">({calculateAge(owner.birthdayDate)} ans)</span>
                    )}
                  </>
                ) : '-' }
              </td>
              <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                {owner.role || '-'}
              </td>
            </tr>
          ))}
          {filteredOwners.length === 0 && (
            <tr>
              <td colSpan={onSelect ? 5 : 4} className="px-6 py-4 text-sm text-gray-500">Aucun résultat</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PhysicalOwnersTable;


